import { computed, readonly, useHead, useState, useNuxtApp } from '#imports'

interface DialogContextState {
    dialogId: string
    ssr?: true
}

export const useDialogContext = (id?: string) => {
    const nuxtApp = useNuxtApp()
    const dialogs = useState<DialogContextState[]>('core:dialogs', () => [])

    const hasOpenDialogs = computed(() => !!dialogs.value.length)

    useHead({
        htmlAttrs: {
            class: () => ({
                'dialog-open': hasOpenDialogs.value
            })
        }
    })

    // SSR useId() can differ from the client's, so a server entry's close() never matches it (FRO-560). Hydrated open dialogs re-register on mount.
    if (nuxtApp.isHydrating) {
        nuxtApp.hooks.hookOnce('app:suspense:resolve', () => {
            dialogs.value = dialogs.value.filter(dialog => !dialog.ssr)
        })
    }

    function open() {
        if (!id) return
        // Don't add multiple instances of the same dialog; a hydrating dialog takes over its own server entry
        if (dialogs.value.find(dialog => dialog.dialogId === id && !dialog.ssr)) return

        // Add dialog as object with id as key to dialogs array
        dialogs.value = [
            ...dialogs.value.filter(dialog => dialog.dialogId !== id),
            import.meta.server ? { dialogId: id, ssr: true } : { dialogId: id },
        ]
    }

    function close() {
        // Remove dialog from dialogs array
        dialogs.value = dialogs.value.filter(dialog => dialog.dialogId !== id)
    }

    return {
        dialogs: readonly(dialogs),
        hasOpenDialogs,
        open,
        close,
    }
}
